import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { PageLimitFilter } from '@typings';
import { useCallback, useEffect, useState } from 'react';

import OpenNotification from 'src/app/components/antd/OpenNotification';
import { OrderBySortOrder } from 'src/app/constants/order';
import { PageListSort } from 'src/app/framework/templates/PageList/PageList.interface';
import { tenantsApi } from 'src/features/tenants/api';
import {
  TenantListItem,
  TenantListProps,
} from 'src/features/tenants/types/tenantList.types';

const PAGE_SIZE = 12;

const DEFAULT_PAGE_LIMIT_FILTER: PageLimitFilter = {
  page: 1,
  limit: PAGE_SIZE,
  order: '["id", "DESC"]',
};

export function useTenantList() {
  const [pageLimitFilter, setPageLimitFilters] = useState<PageLimitFilter>();
  const [pageSort, setPageSort] = useState<PageListSort | undefined>({
    field: 'id',
    order: 'descend',
  });
  const [tenantToToggleStatus, setTenantToToggleStatus] = useState<
    TenantListItem | undefined
  >();

  const { isFetching, data, refetch } = useQuery({
    queryKey: ['tenant-list', pageLimitFilter],
    queryFn: async () => {
      return await tenantsApi.getTenantList(
        pageLimitFilter ?? DEFAULT_PAGE_LIMIT_FILTER
      );
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const handleChangePagination: TenantListProps['onChange'] = useCallback(
    (take: number, _: any, pagination: number) => {
      setPageLimitFilters({
        ...(pageLimitFilter ?? DEFAULT_PAGE_LIMIT_FILTER),
        limit: take,
        page: pagination,
        order:
          pageSort && pageSort.order
            ? `["${pageSort.field}", "${OrderBySortOrder[pageSort.order]}"]`
            : DEFAULT_PAGE_LIMIT_FILTER.order,
      });
    },
    [pageLimitFilter, pageSort]
  );

  const handleChangeSort: TenantListProps['onChangeSort'] = useCallback(
    (sort: PageListSort) => {
      setPageSort(sort);
      setPageLimitFilters({
        ...(pageLimitFilter ?? DEFAULT_PAGE_LIMIT_FILTER),
        order:
          sort && sort.order && sort.order !== null
            ? `["${sort.field}", "${OrderBySortOrder[sort.order]}"]`
            : DEFAULT_PAGE_LIMIT_FILTER.order,
      });
    },
    [pageLimitFilter]
  );

  const handleChangeFilter = useCallback(
    (filterKey: string, filterValue?: string) => {
      setPageSort({
        field: 'id',
        order: 'descend',
      });
      setPageLimitFilters({
        ...DEFAULT_PAGE_LIMIT_FILTER,
        filter: {
          ...pageLimitFilter?.filter,
          [filterKey]: filterValue,
        },
        order: DEFAULT_PAGE_LIMIT_FILTER.order,
      });
    },
    [pageLimitFilter]
  );

  useEffect(() => {
    if (pageLimitFilter) {
      refetch();
    }
  }, [pageLimitFilter]);
  const handleBeforeDisableTenant = (tenant: TenantListItem) => {
    setTenantToToggleStatus(tenant);
  };

  const { mutate: handleToggleTenantStatus } = useMutation({
    mutationFn: async () => {
      if (!tenantToToggleStatus) throw new Error('Tenant not found');
      const { id } = tenantToToggleStatus;
      await tenantsApi.changeTenantStatus(id);
    },
    onSuccess: () => {
      refetch();
      OpenNotification(false, 'Sucesso!', 'Tenant atualizado com sucesso.');
    },
    onError: error => {
      console.error(error);
      OpenNotification(
        true,
        'Ocorreu um erro',
        'Tivemos ao atualizar o tenant, por favor acione nosso suporte.'
      );
    },
    onSettled: () => {
      setTenantToToggleStatus(undefined);
    },
  });

  const handleCancelToggleTenantStatus = () => {
    setTenantToToggleStatus(undefined);
  };

  return {
    isLoading: isFetching,
    tenantList: data,
    pageSize: PAGE_SIZE,
    currentPage: pageLimitFilter?.page,
    pageSort,
    tenantToToggleStatus,
    handleChangePagination,
    handleChangeFilter,
    handleChangeSort,
    handleBeforeDisableTenant,
    handleToggleTenantStatus,
    handleCancelToggleTenantStatus,
  };
}
